import * as React from 'react';
import { useEnvironment } from '@resi-sites/contexts/environmentContext';
import { useLocalStorage } from 'usehooks-ts';
import { INT_CHANNEL_ID, INT_EMBED_ID, PROD_CHANNEL_ID, PROD_EMBED_ID } from '@preview/constants';
import {
  createApiMock,
  createChannelMock,
  createPlaylistMock,
  createSiteMock,
  createVideoMock,
} from '@preview/helpers/apiHelpers';
import { getDefaultMocks, defaultMockConfig } from '@preview/helpers/getDefaultMocks';
import type {
  MockData,
  MockConfig,
  MockItem,
  CreateChannelMock,
  CreatePlaylistMock,
  CreateVideoMock,
  CreateSiteMock,
  CreateApiMock,
} from '@preview/types';

type MockDataContextType = {
  activeMocks: string[];
  addApiMock: (data: CreateApiMock<unknown>, create?: boolean) => void;
  addChannelMock: (data: CreateChannelMock, create?: boolean) => void;
  addPlaylistMock: (data: CreatePlaylistMock, create?: boolean) => void;
  addSiteMock: (data: CreateSiteMock, create?: boolean) => void;
  addVideoMock: (data: CreateVideoMock, create?: boolean) => void;
  channelId: string;
  clearAllMocks: () => void;
  deleteMock: (id: string) => void;
  embedId: string;
  exportStore: () => string;
  findMockForEndpoint: (endpoint: string) => MockItem | null;
  importStore: (jsonData: string) => boolean;
  isMockActive: (id: string) => boolean;
  mockConfig: MockConfig;
  mocks: MockData;
  pendingChanges: number;
  resetPendingChanges: () => void;
  resetToDefaults: () => void;
  toggleMock: (id: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateMock: (id: string, data: any) => void;
  updateMockConfig: (config: Partial<MockConfig>) => void;
};

const MockDataContext = React.createContext<MockDataContextType | undefined>(undefined);

export const MockDataProvider: React.FC<{ children: React.ReactNode; debug?: boolean }> = ({
  children,
  debug = false,
}) => {
  // Use refs to track initialization status
  const { resiStorageKey } = useEnvironment();
  const MOCKS_STORAGE_KEY = `${resiStorageKey}-preview-mock-data`;
  const ACTIVE_MOCKS_STORAGE_KEY = `${resiStorageKey}-preview-active-mocks`;
  const MOCK_CONFIG_STORAGE_KEY = `${resiStorageKey}-preview-mock-config`;
  const isInt = resiStorageKey.includes('int');
  const embedId = isInt ? INT_EMBED_ID : PROD_EMBED_ID;
  const channelId = isInt ? INT_CHANNEL_ID : PROD_CHANNEL_ID;
  const defaultMocks = React.useMemo(() => getDefaultMocks(resiStorageKey), [resiStorageKey]);
  const [mocks, setMocks] = useLocalStorage<MockData>(MOCKS_STORAGE_KEY, defaultMocks);
  const [activeMocks, setActiveMocks] = useLocalStorage<string[]>(ACTIVE_MOCKS_STORAGE_KEY, []);
  const [mockConfig, setMockConfig] = useLocalStorage<MockConfig>(MOCK_CONFIG_STORAGE_KEY, defaultMockConfig);
  const [pendingChanges, setPendingChanges] = React.useState<number>(0);

  const updateMocks = React.useCallback(
    (newMock: MockItem, create: boolean) => {
      const { id } = newMock;
      if (!create) {
        setMocks((prevMocks) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!prevMocks[id]) {
            debug && console.warn(`[MockData] Attempted to update non-existent mock: ${id}`);
            return prevMocks;
          }

          const updatedMocks = {
            ...prevMocks,
            [id]: {
              ...newMock,
              updatedAt: new Date().toISOString(),
            },
          };

          setMocks(updatedMocks);
          return updatedMocks;
        });

        setPendingChanges((prev) => prev + 1);
        debug && console.info(`[MockData] Updated mock: ${id}`);
      }

      setMocks((prevMocks) => {
        const updatedMocks = {
          ...prevMocks,
          [id]: {
            ...newMock,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        };
        return updatedMocks;
      });

      // Activate the new mock, deactivating any conflicting mock with the same endpoint
      setActiveMocks((prev) => {
        const conflicting = prev.filter(
          (activeId) => activeId !== id && (mocks[activeId] as MockItem | undefined)?.endpoint === newMock.endpoint
        );
        const updatedActiveMocks = [...prev.filter((mockId) => !conflicting.includes(mockId)), id];
        return updatedActiveMocks;
      });

      setPendingChanges((prev) => prev + 1);
      debug && console.info(`[MockData] Added mock: ${id}`);
    },
    [debug, mocks, setActiveMocks, setMocks]
  );

  const addPlaylistMock = React.useCallback(
    (mock: Pick<CreatePlaylistMock, 'data' | 'endpoint'>, create = true) => {
      const newPlaylistMock = createPlaylistMock(mock);
      updateMocks(newPlaylistMock, create);
    },
    [updateMocks]
  );
  const addVideoMock = React.useCallback(
    (mock: Pick<CreateVideoMock, 'data' | 'endpoint'>, create = true) => {
      const newVideoMock = createVideoMock(mock);
      updateMocks(newVideoMock, create);
    },
    [updateMocks]
  );

  const addSiteMock = React.useCallback(
    (mock: Pick<CreateSiteMock, 'data' | 'endpoint'>, create = true) => {
      const newSiteMock = createSiteMock(mock);
      updateMocks(newSiteMock, create);
    },
    [updateMocks]
  );

  const addApiMock = React.useCallback(
    (mock: CreateApiMock<unknown>, create = true) => {
      const newApiMock = createApiMock(mock);
      updateMocks(newApiMock, create);
    },
    [updateMocks]
  );

  const addChannelMock = React.useCallback(
    (mock: Pick<CreateChannelMock, 'data' | 'endpoint'>, create = true) => {
      const newChannelMock = createChannelMock(mock);
      updateMocks(newChannelMock, create);
    },
    [updateMocks]
  );

  const updateMockData = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: string, data: any) => {
      setMocks((prevMocks) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!prevMocks[id]) {
          debug && console.warn(`[MockData] Attempted to update non-existent mock: ${id}`);
          return prevMocks;
        }

        const updatedMocks = {
          ...prevMocks,
          [id]: {
            ...prevMocks[id],
            data,
            updatedAt: new Date().toISOString(),
          },
        };

        setMocks(updatedMocks);
        return updatedMocks;
      });

      setPendingChanges((prev) => prev + 1);
      debug && console.info(`[MockData] Updated mock: ${id}`);
    },
    [debug, setMocks]
  );

  // Delete a mock
  const deleteMock = React.useCallback(
    (id: string) => {
      setMocks((prevMocks) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!prevMocks[id]) {
          debug && console.warn(`[MockData] Attempted to delete non-existent mock: ${id}`);
          return prevMocks;
        }

        const { [id]: _, ...rest } = prevMocks;
        setMocks(rest);
        return rest;
      });

      // Remove from active mocks if present
      setActiveMocks((prev) => {
        const updatedActiveMocks = prev.filter((mockId) => mockId !== id);
        setActiveMocks(updatedActiveMocks);
        return updatedActiveMocks;
      });

      setPendingChanges((prev) => prev + 1);
      debug && console.info(`[MockData] Deleted mock: ${id}`);
    },
    [debug, setActiveMocks, setMocks]
  );

  // Toggle a mock's active state
  const toggleMock = React.useCallback(
    (id: string) => {
      setActiveMocks((prev) => {
        const isActive = prev.includes(id);
        let updatedActiveMocks: string[];

        if (isActive) {
          updatedActiveMocks = prev.filter((mockId) => mockId !== id);
        } else {
          // Deactivate any other active mocks with the same endpoint to prevent conflicts
          const endpoint = mocks[id].endpoint;
          const conflicting = endpoint ? prev.filter((activeId) => mocks[activeId].endpoint === endpoint) : [];
          updatedActiveMocks = [...prev.filter((mockId) => !conflicting.includes(mockId)), id];
        }

        setActiveMocks(updatedActiveMocks);

        setPendingChanges((prev) => prev + 1);
        debug && console.info(`[MockData] Toggled mock ${id}: ${!isActive ? 'active' : 'inactive'}`);
        return updatedActiveMocks;
      });
    },
    [debug, mocks, setActiveMocks]
  );

  // Check if a mock is active
  const isMockActive = React.useCallback(
    (id: string) => {
      return activeMocks.includes(id);
    },
    [activeMocks]
  );

  // Clear all mocks except token mocks
  const clearAllMocks = React.useCallback(() => {
    setMocks((prevMocks) => {
      // Filter to keep only token mocks
      const tokenMocks = Object.entries(prevMocks)
        .filter(([id]) => id.includes('token'))
        .reduce((acc, [id, mock]) => ({ ...acc, [id]: mock }), {});

      setMocks(tokenMocks);
      return tokenMocks;
    });

    setActiveMocks((prevActiveMocks) => {
      // Keep only token mock IDs in active mocks
      const tokenMockIds = prevActiveMocks.filter((id) => id.includes('token'));
      setActiveMocks(tokenMockIds);
      return tokenMockIds;
    });

    setPendingChanges(0);
    debug && console.info('[MockData] Cleared all non-token mocks');
  }, [debug, setActiveMocks, setMocks]);

  // Reset to default mocks
  const resetToDefaults = React.useCallback(() => {
    // Activate all defaults except mock_channels_live_event — it conflicts with
    // mock_channels_no_event (same endpoint), and only one channel mock should be
    // active at a time. Users can toggle it on explicitly when they need it.
    const defaultActiveKeys = Object.keys(defaultMocks).filter((k) => k !== 'mock_channels_live_event');
    setMocks(defaultMocks);
    setActiveMocks(defaultActiveKeys);
    setPendingChanges(0);
    setMocks(defaultMocks);
    setActiveMocks(defaultActiveKeys);
    debug && console.info('[MockData] Reset to default mocks');
  }, [debug, defaultMocks, setActiveMocks, setMocks]);

  // Update mock configuration
  const updateMockConfig = React.useCallback(
    (config: Partial<MockConfig>) => {
      setMockConfig((prev) => {
        const updatedConfig = { ...prev, ...config };
        setMockConfig(updatedConfig);

        setPendingChanges((prev) => prev + 1);
        debug && console.info('[MockData] Updated mock config:', config);
        return updatedConfig;
      });
    },
    [debug, setMockConfig]
  );

  // Reset pending changes counter
  const resetPendingChanges = React.useCallback(() => {
    setPendingChanges(0);
    debug && console.info('[MockData] Reset pending changes counter');
  }, [debug]);

  const findMockForEndpoint = React.useCallback(
    (endpoint: string): MockItem | null => {
      // Only consider active mocks. Reverse so the most recently activated mock wins
      // when multiple mocks share the same endpoint pattern.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const activeMockItems = [...activeMocks]
        .reverse()
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        .filter((id) => mocks[id])
        .map((id) => mocks[id]);

      // First try exact match
      const exactMatch = activeMockItems.find((mock) => mock.endpoint === endpoint);
      if (exactMatch) return exactMatch;

      // Then try pattern matching (simple for now, could be enhanced with regex)
      // For example, match /api/videos/* to /api/videos/123
      const patternMatch = activeMockItems.find((mock) => {
        if (mock.endpoint.endsWith('*') && mock.endpoint.startsWith('*')) {
          const pattern = mock.endpoint.slice(1, -1);
          return endpoint.includes(pattern);
        }

        if (mock.endpoint.startsWith('*')) {
          const pattern = mock.endpoint.slice(1);
          return endpoint.endsWith(pattern);
        }

        if (mock.endpoint.endsWith('*')) {
          const pattern = mock.endpoint.slice(0, -1);
          return endpoint.startsWith(pattern);
        }
        return false;
      });

      return patternMatch || null;
    },
    [mocks, activeMocks]
  );

  // Export the entire store as a JSON string
  const exportStore = React.useCallback(() => {
    const storeData = {
      mocks,
      activeMocks,
      mockConfig,
    };
    return JSON.stringify(storeData, null, 2);
  }, [mocks, activeMocks, mockConfig]);

  // Import store data from a JSON string
  const importStore = React.useCallback(
    (jsonData: string) => {
      try {
        const parsedData = JSON.parse(jsonData);

        // Validate the imported data structure
        if (!parsedData.mocks || !parsedData.activeMocks || !parsedData.mockConfig) {
          throw new Error('Invalid store data format');
        }

        // Update the store with imported data
        setMocks(parsedData.mocks);
        setActiveMocks(parsedData.activeMocks);
        setMockConfig(parsedData.mockConfig);

        // Persist to storage
        // setMocks(parsedData.mocks);
        // mockStore.setActiveMocks(parsedData.activeMocks);
        // mockStore.setMockConfig(parsedData.mockConfig);

        setPendingChanges(0);
        debug && console.info('[MockData] Successfully imported store data');
        return true;
      } catch (error) {
        console.error('[MockData] Error importing store data:', error);
        return false;
      }
    },
    [debug, setActiveMocks, setMockConfig, setMocks]
  );

  const value = {
    mocks,
    channelId,
    embedId,
    activeMocks,
    addChannelMock,
    addPlaylistMock,
    addVideoMock,
    addSiteMock,
    addApiMock,
    deleteMock,
    toggleMock,
    isMockActive,
    clearAllMocks,
    resetToDefaults,
    mockConfig,
    updateMockConfig,
    findMockForEndpoint,
    pendingChanges,
    resetPendingChanges,
    exportStore,
    importStore,
    updateMock: updateMockData,
  };

  return <MockDataContext.Provider value={value}>{children}</MockDataContext.Provider>;
};

export const useMockData = () => {
  const context = React.useContext(MockDataContext);
  if (context === undefined) {
    throw new Error('useMockData must be used within a MockDataProvider');
  }
  return context;
};
